import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, GitMerge, Library } from 'lucide-react';

const Practice = () => {
  const navigate = useNavigate();

  // Data for Languages Section (IDs 1-4)
  const languages = [
    {
      id: 1,
      name: 'C',
      logoText: 'C',
      bgColor: 'bg-[#0066ff]',
      shadowClass: 'shadow-[0_0_30px_-10px_rgba(0,102,255,0.45)] hover:shadow-[0_0_45px_-5px_rgba(0,102,255,0.7)]',
    },
    {
      id: 2,
      name: 'C++',
      logoText: 'C++',
      bgColor: 'bg-[#5b4eff]',
      shadowClass: 'shadow-[0_0_30px_-5px_rgba(91,78,255,0.45)] hover:shadow-[0_0_45px_-5px_rgba(91,78,255,0.7)]',
    },
    {
      id: 3,
      name: 'Java',
      icon: (
        <svg viewBox="0 0 24 24" fill="white" className="w-7 h-7">
          <path d="M15.34 16.63c-1.32.25-3.08.36-4.96.36-2.58 0-4.64-.22-5.5-.6-.27-.12-.4-.28-.38-.45.02-.2.24-.37.6-.5.98-.38 2.87-.58 5.28-.58 1.83 0 3.52.12 4.77.34.84.15 1.48.33 1.94.54.4.18.63.4.63.63 0 .17-.15.34-.45.48-.48.22-1.2.39-1.93.5v-.72zm1.6-1.5c-1.28-.48-3.4-.78-5.94-.78-2.67 0-4.93.3-6.26.8-1.07.4-1.7 1-1.7 1.63 0 .66.66 1.28 1.8 1.7 1.44.53 3.65.82 6.16.82 2.45 0 4.6-.28 6-.78 1.1-.42 1.74-1.02 1.74-1.68 0-.64-.6-1.24-1.8-1.71v-.02zm-5.94 4.5c-3.1 0-5.8-.38-7.2-1.03-1.45-.66-2.28-1.5-2.28-2.4 0-1.03 1.05-1.98 2.83-2.6 1.6-.57 4.02-.9 6.65-.9 2.7 0 5.14.35 6.78.94 1.72.6 2.72 1.54 2.72 2.56 0 .9-.8 1.74-2.2 2.4-1.35.64-4.04 1.03-7.3 1.03z" />
          <path d="M14.65 6.6c-.66.66-1.12 1.7-1.12 2.92 0 1.25.48 2.3 1.17 2.98.66.67 1.53 1 2.4 1 .84 0 1.68-.32 2.34-.98.7-.68 1.15-1.72 1.15-2.95 0-1.28-.5-2.35-1.2-3.05-.66-.67-1.54-1.02-2.42-1.02-.85 0-1.68.34-2.32 1.1zm3.84 5.25c-1.03 1.03-2.8 1.03-3.84 0-.52-.52-.8-1.3-.8-2.18 0-.85.28-1.63.8-2.15.52-.52 1.2-.8 1.92-.8.74 0 1.42.28 1.94.8.5.53.8 1.32.8 2.18 0 .86-.3 1.63-.82 2.15z" />
        </svg>
      ),
      shadowClass: 'shadow-[0_0_30px_-10px_rgba(255,85,0,0.45)] hover:shadow-[0_0_45px_-5px_rgba(255,85,0,0.7)]',
    },
    {
      id: 4,
      name: 'Python',
      icon: (
        <svg viewBox="0 0 24 24" fill="white" className="w-7 h-7">
          <path d="M12.03.018c-3.132 0-3.528.135-4.78.414-1.666.36-1.983 1.15-1.983 2.65v2.85h6.9v1H5.165C3.125 6.932.9 8.01.9 11.53c0 3.52 1.833 4.6 3.86 4.6h1.408V13.8c0-2.364 2.115-3.64 4.148-3.64h4.155c1.196 0 2.21-.937 2.21-2.115V3.86c0-1.428-1.036-2.583-2.463-2.583H12.03zm-1.8 1.7a1.05 1.05 0 1 1 0 2.1 1.05 1.05 0 0 1 0-2.1z" />
          <path d="M11.97 23.98c3.13 0 3.527-.133 4.778-.412 1.665-.36 1.982-1.15 1.982-2.65v-2.85H11.83v-1h7.002c2.04 0 4.267-1.078 4.267-4.598 0-3.52-1.832-4.6-3.858-4.6h-1.408v2.33c0 2.364-2.116 3.64-4.148 3.64h-4.156c-1.196 0-2.21.936-2.21 2.114v4.184c0 1.428 1.035 2.582 2.462 2.582h2.2zM13.77 20.18a1.05 1.05 0 1 1 0 2.1 1.05 1.05 0 0 1 0-2.1z" />
        </svg>
      ),
      shadowClass: 'shadow-[0_0_30px_-5px_rgba(255,179,0,0.45)] hover:shadow-[0_0_45px_-5px_rgba(255,179,0,0.7)]',
    },
  ];

  // Data for Tracks Section (IDs 5-7)
  const tracks = [
    {
      id: 5,
      name: '100 Basic Problems',
      icon: <FileText className="w-7 h-7 text-white" />,
      bgColor: 'bg-[#00c853]',
      shadowClass: 'shadow-[0_0_30px_-5px_rgba(0,200,83,0.45)] hover:shadow-[0_0_45px_-5px_rgba(0,200,83,0.7)]',
    },
    {
      id: 6,
      name: '100 DSA Problems',
      icon: <GitMerge className="w-7 h-7 text-white transform rotate-90" />,
      bgColor: 'bg-[#a344ff]',
      shadowClass: 'shadow-[0_0_30px_-5px_rgba(163,68,255,0.45)] hover:shadow-[0_0_45px_-5px_rgba(163,68,255,0.7)]',
    },
    {
      id: 7,
      name: '100 OOP Problems',
      icon: <Library className="w-7 h-7 text-white" />,
      bgColor: 'bg-[#ff5500]',
      shadowClass: 'shadow-[0_0_30px_-5px_rgba(255,85,0,0.45)] hover:shadow-[0_0_45px_-5px_rgba(255,85,0,0.7)]',
    },
  ];

  return (
    <div className="min-h-screen bg-[#f9fbff] mt-20 flex flex-col items-center py-12 px-4 selection:bg-[#00b4d8] selection:text-white">
      {/* 1. Header & Search Section */}
      <div className="text-center w-full max-w-2xl mb-12">
        <p className="font-outfit text-[#0088cc] font-bold text-[10px] md:text-xs tracking-[0.2em] uppercase mb-2">
          Level up your skills
        </p>
        <h1 className="font-outfit text-3xl md:text-5xl font-extrabold mb-3 text-[#111827] tracking-tight">
          Programming{' '}
          <span className="bg-gradient-to-r from-[#00b4d8] to-[#00c853] text-transparent bg-clip-text">
            Practice
          </span>
        </h1>
        <p className="font-dmsans text-gray-500 text-sm md:text-base mb-8 max-w-lg mx-auto leading-relaxed">
          Select a language or a track to begin solving problems, from basic syntax
          challenges to complex algorithmic puzzles.
        </p>

        {/* Search Bar */}
        <div className="relative max-w-md mx-auto p-[2px] rounded-xl bg-gradient-to-r from-[#00b4d8] to-[#00c853] shadow-[0_10px_25px_-5px_rgba(0,180,216,0.2)]">
          <div className="relative bg-white rounded-[10px] group transition-all duration-300 overflow-hidden">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
              <Search className="h-4.5 w-4.5 text-gray-400 group-focus-within:text-[#0088cc] transition-colors" />
            </div>
            <input
              type="text"
              className="font-jakarta block w-full pl-12 pr-4 py-3.5 bg-white text-gray-900 placeholder-gray-400 focus:outline-none text-sm transition-all"
              placeholder="Search for a language or track..."
            />
          </div>
        </div>
      </div>

      {/* 2. Practice by Language Section */}
      <div className="w-full max-w-4xl mb-16">
        <h2 className="font-outfit text-xl font-bold text-center text-[#1f2937] mb-8 tracking-wide">
          Practice by Language
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {languages.map((lang) => (
            <div
              key={lang.id}
              onClick={() => navigate(`/practice/${lang.id}`)}
              className={`group bg-white rounded-3xl p-6 flex flex-col items-center justify-center text-center border border-gray-100/50 hover:-translate-y-2 transition-all duration-500 cursor-pointer ${lang.shadowClass}`}
            >
              <div
                className={`w-16 h-16 rounded-full flex items-center justify-center mb-5 shadow-inner ${lang.bgColor} ring-4 ring-white group-hover:scale-110 transition-transform duration-500`}
              >
                {lang.icon ? (
                  lang.icon
                ) : (
                  <span className="font-jakarta text-white text-xl font-bold">{lang.logoText}</span>
                )}
              </div>
              <h3 className="font-outfit text-lg font-bold text-gray-800 mb-1.5 group-hover:text-[#111827] transition-colors">{lang.name}</h3>
              <p className="font-jakarta text-gray-400 text-[10px] font-bold flex items-center gap-1 group-hover:text-[#0088cc] transition-colors uppercase tracking-widest">
                View Problems <span className="text-sm leading-none group-hover:translate-x-1 transition-transform">&rarr;</span>
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="w-20 h-[1px] bg-gray-200 mb-16 opacity-50"></div>

      {/* 3. Practice by Track Section */}
      <div className="w-full max-w-4xl mb-8">
        <h2 className="font-outfit text-xl font-bold text-center text-[#1f2937] mb-8 tracking-wide">
          Practice by Track
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
          {tracks.map((track) => (
            <div
              key={track.id}
              onClick={() => navigate(`/practice/${track.id}`)}
              className={`group bg-white rounded-3xl p-6 flex flex-col items-center justify-center text-center border border-gray-100/50 hover:-translate-y-2 transition-all duration-500 cursor-pointer ${track.shadowClass}`}
            >
              <div
                className={`w-16 h-16 rounded-full flex items-center justify-center mb-5 shadow-inner ${track.bgColor} ring-4 ring-white group-hover:scale-110 transition-transform duration-500`}
              >
                {track.icon}
              </div>
              <h3 className="font-outfit text-lg font-bold text-gray-800 mb-1.5 group-hover:text-[#111827] transition-colors">{track.name}</h3>
              <p className="font-jakarta text-gray-400 text-[10px] font-bold flex items-center gap-1 group-hover:text-[#0088cc] transition-colors uppercase tracking-widest">
                Start Track <span className="text-sm leading-none group-hover:translate-x-1 transition-transform">&rarr;</span>
              </p>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .font-outfit { font-family: 'Outfit', sans-serif; }
        .font-dmsans { font-family: 'DM Sans', sans-serif; }
        .font-jakarta { font-family: 'Plus Jakarta Sans', sans-serif; }
      `}</style>
    </div>
  );
};

export default Practice;